// components/charts/FeedbackRenderChart.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store";
import { parseFeedbackStats } from "utils/parsers";


ChartJS.register(ArcElement, Tooltip, Legend);

// ─── Colors ───
const FEEDBACK_COLORS: {
  [key: string]: { background: string; border: string };
} = {
  Excellent: {
    background: "rgba(10, 189, 105, 1)",
      border: 'rgba(200, 200, 200, 0.15)',
  },
  "Very Good": {
    background: "rgba(54, 162, 235, 1)",
       border: 'rgba(200, 200, 200, 0.15)',
  },
  Good: {
    background: "rgba(255, 206, 86, 1)",
   border: 'rgba(200, 200, 200, 0.15)',
  },
  Fair: {
    background: "rgba(255, 165, 0, 1)",
       border: 'rgba(200, 200, 200, 0.15)',
  },
  Poor: {
    background: "rgba(243, 8, 8, 1)",
      border: 'rgba(200, 200, 200, 0.15)',
  },
};

const DEFAULT_COLOR = {
  background: "rgba(128, 128, 128, 1)",
    border: 'rgba(200, 200, 200, 0.15)',
};

const getFeedbackColor = (category: string) =>
  FEEDBACK_COLORS[category] || DEFAULT_COLOR;

export default function FeedbackRenderChart() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const chartRef = useRef(null);
  const dispatch = useDispatch();


  const {
    users: { feedbackStats },
  } = useSelector<AppState, AppState>((state) => state);
console.log("Feedback stats from store: ", feedbackStats);
  const stats = useMemo(
    () => parseFeedbackStats(feedbackStats || {}),
    [feedbackStats]
  );


  // ─── Resize listener ───
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isExtraSmall = windowWidth < 300;
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // ─── Filter zero-count for chart only ───
  const nonZeroStats = useMemo(
    () => stats.filter((s: { count: number; }) => s.count > 0),
    [stats]
  );

  const totalFeedback = stats.reduce((acc: any, curr: { count: any; }) => acc + curr.count, 0);

  // ─── Chart data (same pattern as pieChartData in vehicles) ───
  const pieChartData = useMemo(() => {
    const dataToUse = nonZeroStats.length > 0 ? nonZeroStats : stats;
    const categories = dataToUse.map((s: { category: any; }) => s.category);

    return {
      labels: categories,
      datasets: [
        {
          data: dataToUse.map((s: { count: any; }) => s.count),
          backgroundColor: categories.map(
            (c: string) => getFeedbackColor(c).background
          ),
          borderColor: categories.map(
            (c: string) => getFeedbackColor(c).border
          ),
          borderWidth: 2,
          hoverOffset: isMobile ? 5 : 10,
        },
      ],
    };
  }, [nonZeroStats, stats, isMobile]);

  // ─── Chart options (same pattern as vehicles) ───
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "55%",
      layout: {
        padding: isExtraSmall ? 1 : isMobile ? 3 : 5,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label(context: any) {
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce(
                (a: number, b: number) => a + b,
                0
              );
              const pct =
                total > 0
                  ? ((value / total) * 100).toFixed(1)
                  : "0";
              return `${context.label}: ${value} feedbacks (${pct}%)`;
            },
          },
          bodyFont: {
            size: isExtraSmall ? 9 : isMobile ? 10 : 12,
          },
        },
      },
      onClick: (_event: any, elements: any) => {
        if (elements.length > 0) {
          const idx = elements[0].index;
          const dataToUse =
            nonZeroStats.length > 0 ? nonZeroStats : stats;
          const clicked = dataToUse[idx];
          console.log("Clicked:", clicked.category, "Count:", clicked.count);
        }
      },
    }),
    [isExtraSmall, isMobile, nonZeroStats, stats]
  );

  // ─── Responsive height ───
  const getChartHeight = () => {
    if (isExtraSmall) return "200px";
    if (isMobile) return "250px";
    if (isTablet) return "280px";
    return "150px";
  };

  // ─── Render ───
  return (
    <div className="feedback-chart-container">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <h3 style={{ margin: 0, fontSize: isMobile ? "14px" : "16px" }}>
          User Feedback
        </h3>
        {totalFeedback > 0 && (
          <span
            style={{
              fontSize: isMobile ? 11 : 13,
              color: "#888",
              background: "#f5f5f5",
              padding: "2px 8px",
              borderRadius: "10px",
            }}
          >
            Total: {totalFeedback}
          </span>
        )}
      </div>

      {/* Chart */}
      <div className="pie-chart-wrapper">
        {nonZeroStats.length > 0 ? (
          <div
            className="pie-chart-container"
            style={{
              height: getChartHeight(),
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              padding: isMobile ? "10px" : "0",
            }}
          >
            <Doughnut
              ref={chartRef}
              data={pieChartData}
              options={chartOptions}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "150px",
              textAlign: "center",
              color: "#999",
            }}
          >
            <p>No feedback data available.</p>
          </div>
        )}
      </div>

      {/* ✅ Stats Cards  */}
      {nonZeroStats.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, minmax(0, 1fr))"
              : `repeat(${Math.min(stats.length, 5)}, minmax(0, 1fr))`,
            gap: isMobile ? "6px" : "8px",
            marginTop: "15px",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {stats.map((stat: { key: string; category: string; count: number }) => {
            const color = getFeedbackColor(stat.category);
            return (
              <div
                key={stat.key}
                style={{
                  textAlign: "center",
                  padding: isMobile ? "6px 4px" : "8px 4px",
                  borderRadius: "8px",
                  background: color.border,
                  cursor: "default",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  border: `1px solid ${color.background}20`,
                  minWidth: 0,
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow = `0 2px 8px ${color.background}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? "16px" : "20px",
                    fontWeight: 700,
                    color: color.background,
                  }}
                >
                  {stat.count}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "9px" : "11px",
                    color: "#555",
                    marginTop: "2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                  title={stat.category}
                >
                  {stat.category}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}